/**
 * TimelineDragController — drag-and-drop reordering for Timeline boxes.
 * The horizontal counterpart of recipe_panels/PreparationDragController —
 * same rules, same architecture, transposed from vertical/DOM-flow to
 * horizontal/SVG-transform:
 *
 * - The dragged node's own box(es) track the pointer 1:1 on the X axis
 *   (constrained — Y, i.e. `row`, never changes from a horizontal drag),
 *   raised to the front so they paint on top of everything else.
 * - A dashed placeholder rect shows where the group would land.
 * - Swapping with a neighbor triggers when the group's LEADING edge on
 *   that side (groupLeft when a neighbor sits to the left, groupRight
 *   when one sits to the right — including any attached dependencies)
 *   crosses that neighbor's own midpoint — not the group's center, and
 *   not the neighbor's edge: see PreparationDragController's own
 *   resolveSwapAnchor() doc comment for why both of those choices matter
 *   (flicker-free, and correct once the group has grown).
 * - Passing over a dependency boundary LIFTS that dependency's box(es)
 *   too, attaching them to the dragged group (growing the placeholder)
 *   rather than just letting it reflow in place — same cascade-pickup
 *   behavior as the Elaboració panel.
 *
 * Deliberately simpler than its vertical counterpart in two respects:
 * - No "settled vs. mid-transition" DOM-measurement caveat at all. The
 *   vertical version had to cache a `settledRect` because it read
 *   another row's position via getBoundingClientRect(), which reflects
 *   whatever's currently PAINTED (an interpolating value, mid a CSS FLIP
 *   transition) rather than the row's true logical position. Here,
 *   layout is computed from `LayoutSegment.pxStart/pxWidth` straight out
 *   of TimelineView's own pure computeLayout() (never a live DOM read),
 *   and `settledLayout` below only ever gets explicitly-numeric values
 *   written into it by applyHypotheticalLayout() — no transition can
 *   observably corrupt it. D3's own transition (used for the move
 *   animation) is purely cosmetic here for the exact same reason.
 * - No DOM-order/adjacency bookkeeping (no `findAdjacentRow`-style stale-
 *   sibling walk). Box position is explicit (an SVG `transform`), fully
 *   decoupled from DOM child order, so "who's the immediate neighbor" is
 *   just an index lookup into `lastOrder` rather than a DOM traversal.
 *
 * No auto-pan-near-the-edge equivalent (yet) — panning itself is already
 * available (from anywhere a drag ISN'T currently engaging, see
 * TimelineView's own zoom filter), so reaching further content during a
 * drag isn't impossible, just not automatic the way the Elaboració
 * panel's auto-scroll is. Can be added later if it turns out to matter.
 */

export interface TimelineDragCallbacks {
  /** Fired whenever a drag starts or ends (past the movement threshold). */
  onDragStateChange: (dragging: boolean) => void;
  /**
   * Read-only live preview: given a hypothetical drop placing `nodeId`
   * immediately before/after `anchorNodeId` (both rendered-box node
   * ids), the full resulting node-id order plus whichever OTHER node
   * ids the dependency cascade forces to move alongside `nodeId`. Same
   * contract as PreparationDragController's own `previewOrder`.
   */
  previewOrder: (nodeId: string, anchorNodeId: string | null, insertBefore: boolean) => { order: string[]; cascadedIds: string[] };
  /**
   * Given a full hypothetical node-id order, the resulting pxStart/
   * pxWidth of EVERY segment (keyed by its stable `stepNumber`) —
   * TimelineView's own computeLayout() re-run against that order. Used
   * both to reposition still-in-place boxes and to find the lifted
   * group's own hypothetical slot (for the placeholder).
   */
  layoutForOrder: (nodeOrder: string[]) => Map<number, { pxStart: number; pxWidth: number }>;
  /**
   * Pan the view by `deltaPx` screen pixels (positive = reveal more
   * content to the left, negative = more to the right — see
   * updateAutoPan()'s own doc comment) and return how much was actually
   * applied, which can be less than requested once either end of the
   * preparation is reached (TimelineView's own zoom.constrain() clamps
   * it). The caller needs the REAL applied amount to keep the dragged
   * group pinned under the pointer while the content pans underneath it.
   */
  panBy: (deltaPx: number) => number;
  /** Fired with the resolved move on a valid drop — same shape as previewOrder. */
  onDrop: (nodeId: string, anchorNodeId: string | null, insertBefore: boolean) => void;
}

/** Matches TimelineView's own LayoutSegment shape closely enough for this controller's needs — see the datum bound to each `.timeline-box` group. */
interface BoxDatum {
  box: { step: { node: { id: string }; stepNumber: number }; row: number };
  pxStart: number;
  pxWidth: number;
}

interface LiftedBlock {
  els: SVGGElement[];
  /** Each el's pxStart relative to the block's own first el's pxStart — rigid, frozen at lift time. */
  elLeftOffsets: number[];
  /** Last el's (pxStart+width) minus first el's pxStart. */
  blockWidth: number;
  /** translate()'s y component — the row's own CENTER (row * rowHeightPx
   *  + rowHeightPx / 2, matching TimelineView's screenPos()), constant
   *  for the whole drag. */
  y: number;
  /** Added to `pxStart` (a slot's LEFT edge) when actually setting a
   *  transform — half of this block's own (uniform) pxWidth, i.e. the
   *  distance from a slot's left edge to its center. Every TimelineBox
   *  group's OWN transform (see TimelineView's renderBoxes()) is set to
   *  its circle's CENTER, not its slot's left edge (screenPos() adds
   *  NODE_SPACING_PX / 2) — this mirrors that same correction for
   *  whatever THIS controller positions during a drag, which otherwise
   *  ends up rendered half a slot-width left and half a row-height up
   *  from the slot the raw pxStart/row*rowHeightPx data describes. */
  xOffset: number;
}

const DRAG_THRESHOLD = 5;
const MOVE_DURATION_MS = 150;
const AUTOSCROLL_MARGIN = 48;
const AUTOSCROLL_SPEED = 7;
/** The dashed landing-slot placeholder's own fixed height — centered
 *  vertically within the row, same as every node circle now is (see
 *  LiftedBlock.y's own comment); its width always spans a full slot
 *  (or several, for a cascaded group) so, unlike the circle positions
 *  below, it needs no xOffset-style correction. */
const PLACEHOLDER_HEIGHT_PX = 36;

export class TimelineDragController {
  private callbacks: TimelineDragCallbacks;
  private rowHeightPx: number;

  private startX = 0;
  private dragging = false;
  private draggedNodeId: string | null = null;
  private draggedBoxEl: SVGGElement | null = null;

  // Frozen for the duration of one drag gesture, built once in
  // beginDragVisuals(). Map insertion order is the original left-to-
  // right rendered sequence.
  private boxesGroupEl: SVGGElement | null = null;
  private boxesByNodeId: Map<string, SVGGElement[]> = new Map();

  // Every node currently lifted out of normal layout — always includes
  // draggedNodeId, plus whatever the live cascade is currently pulling
  // along with it. All attached to the same floating group, stacked
  // left-to-right together — see layoutLiftedGroup().
  private liftedNodeIds: Set<string> = new Set();
  private liftedBlocks: Map<string, LiftedBlock> = new Map();
  private draggedBlockOriginalLeft = 0;
  // The floating group's current left/right edge, including any
  // attached dependencies — kept live by layoutLiftedGroup() every
  // tick, same reasoning as PreparationDragController's groupTop/
  // groupBottom (the comparison in resolveSwapAnchor() has to track the
  // group's actual current extent, not a stale single-node reference,
  // or attaching/detaching a dependency mid-drag flip-flops).
  private groupLeft = 0;
  private groupRight = 0;
  private currentDeltaX = 0;
  // The most recent real pointer clientX seen (from an actual mousemove/
  // touchmove, NOT a synthetic one) — recomputeCurrentDeltaX() needs
  // this to stay in sync with totalPanAppliedPx below even when IT'S
  // the one driving a recompute (the auto-scroll RAF loop), since the
  // cursor itself hasn't necessarily moved on that tick.
  private lastClientX = 0;
  // Running total of every panBy()-applied delta since this drag
  // started — see recomputeCurrentDeltaX()'s own comment for why
  // currentDeltaX can't just be `clientX - startX` once any panning has
  // happened mid-drag.
  private totalPanAppliedPx = 0;
  // Most recently resolved full hypothetical order — cached so
  // updateDraggedPosition() can keep the floating group's internal
  // sequencing (and resolveSwapAnchor()'s neighbor lookups) stable
  // between resolveSwap() calls (which only recompute it when the
  // target actually changes).
  private lastOrder: string[] = [];
  // A dashed placeholder rect standing in for the WHOLE lifted group in
  // the normal layout — this is what shows "it will land here", sized
  // to match everything currently lifted, and what the surrounding
  // boxes visibly separate for.
  private placeholderEl: SVGRectElement | null = null;
  // Each still-in-place node's current logical [pxStart, pxWidth] span
  // (combined across all its segments) — see this file's own header
  // comment for why this never needs the vertical version's "settled
  // vs. mid-transition" caveat.
  private settledLayout: Map<string, { pxStart: number; pxWidth: number }> = new Map();
  // The svg's own viewport bounds — captured once at drag start — used
  // only to detect "cursor near the left/right edge" for auto-pan;
  // unrelated to panGroup's own (separately tracked, via TimelineView)
  // pan offset.
  private viewportLeft = 0;
  private viewportRight = 0;
  private autoScrollDir: -1 | 0 | 1 = 0;
  private autoScrollRAF: number | null = null;

  private pendingAnchor: { anchorNodeId: string | null; insertBefore: boolean } | null = null;
  private lastResolvedKey: string | null = null;

  constructor(callbacks: TimelineDragCallbacks, rowHeightPx: number) {
    this.callbacks = callbacks;
    this.rowHeightPx = rowHeightPx;
  }

  /** Whether a drag is currently in progress (past the movement threshold). */
  isActive(): boolean {
    return this.dragging;
  }

  /** If a drag is in progress, abort it with no effect. Call before a re-render replaces the DOM out from under it. */
  cancel(): void {
    this.cleanup();
  }

  /** Wire drag-start listeners on a rendered `.timeline-box` group. Call for every box on every full render(). */
  registerBox(el: SVGGElement, nodeId: string): void {
    const start = (clientX: number): void => {
      this.startX = clientX;
      this.dragging = false;
      this.draggedNodeId = nodeId;
      this.draggedBoxEl = el;
    };

    el.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      start(e.clientX);

      const moveHandler = (ev: MouseEvent) => {
        if (!this.dragging) {
          // Only horizontal movement counts toward starting a drag —
          // a purely (or mostly) vertical wiggle never should, since
          // there's nothing vertical to drag here (no track reordering
          // yet — see this plan's own "parallel tracks, later" note).
          const dx = ev.clientX - this.startX;
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          this.beginDrag();
        }
        this.handleMove(ev.clientX);
      };
      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        this.handleUp();
      };
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    });

    // No long-press/scroll disambiguation needed here, unlike the
    // Elaboració panel's handle — touch-action: none is already set on
    // the WHOLE timeline SVG (TimelineView's own pan/zoom setup), so
    // there's no competing native gesture to protect; a tap-vs-drag
    // threshold is all that's needed, same as the mouse path.
    el.addEventListener('touchstart', (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const touchId = touch.identifier;
      start(touch.clientX);

      const moveHandler = (ev: TouchEvent) => {
        const t = Array.from(ev.touches).find(t => t.identifier === touchId);
        if (!t) return;
        if (!this.dragging) {
          const dx = t.clientX - this.startX;
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          this.beginDrag();
        }
        ev.preventDefault();
        this.handleMove(t.clientX);
      };
      const endHandler = () => {
        el.removeEventListener('touchmove', moveHandler);
        el.removeEventListener('touchend', endHandler);
        el.removeEventListener('touchcancel', endHandler);
        this.handleUp();
      };
      el.addEventListener('touchmove', moveHandler, { passive: false });
      el.addEventListener('touchend', endHandler);
      el.addEventListener('touchcancel', endHandler);
    }, { passive: true });
  }

  // ─── Movement / drop resolution ────────────────────────────────

  private beginDrag(): void {
    this.dragging = true;
    this.beginDragVisuals();
    this.callbacks.onDragStateChange(true);
  }

  private handleMove(clientX: number): void {
    if (!this.draggedNodeId || !this.dragging) return;
    this.updateDraggedPosition(clientX);
    this.updateAutoPan(clientX);
    this.resolveSwap();
  }

  private handleUp(): void {
    if (this.dragging && this.draggedNodeId && this.pendingAnchor) {
      this.callbacks.onDrop(this.draggedNodeId, this.pendingAnchor.anchorNodeId, this.pendingAnchor.insertBefore);
    }
    this.cleanup();
  }

  private resolveSwap(): void {
    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId) return;

    const swap = this.resolveSwapAnchor();
    if (!swap) return;
    const { anchorNodeId, insertBefore } = swap;

    const resolvedKey = `${anchorNodeId}:${insertBefore}`;
    this.pendingAnchor = { anchorNodeId, insertBefore };
    if (resolvedKey === this.lastResolvedKey) return;
    this.lastResolvedKey = resolvedKey;

    const { order, cascadedIds } = this.callbacks.previewOrder(draggedNodeId, anchorNodeId, insertBefore);
    this.lastOrder = order;

    const newLiftedIds = new Set([draggedNodeId, ...cascadedIds]);
    for (const id of Array.from(this.liftedNodeIds)) {
      if (id !== draggedNodeId && !newLiftedIds.has(id)) this.releaseNode(id);
    }
    for (const id of newLiftedIds) {
      if (!this.liftedNodeIds.has(id)) this.liftNode(id);
    }

    this.applyHypotheticalLayout(order);
    this.layoutLiftedGroup();
  }

  /**
   * Finds the immediate left/right neighbor (via `lastOrder`, skipping
   * past any other currently-lifted id — cascaded ids are always
   * contiguous with the dragged node, per stepOrder.ts) and swaps with
   * it once the group's edge on that side crosses its midpoint. See
   * this file's own header comment for why this is an array lookup
   * rather than PreparationDragController's DOM-sibling walk.
   */
  private resolveSwapAnchor(): { anchorNodeId: string | null; insertBefore: boolean } | null {
    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId) return null;

    const order = (this.lastOrder.length > 0 ? this.lastOrder : [draggedNodeId])
      .filter(id => this.boxesByNodeId.has(id));
    const idx = order.indexOf(draggedNodeId);
    if (idx === -1) return null;

    let leftIdx = idx - 1;
    while (leftIdx >= 0 && this.liftedNodeIds.has(order[leftIdx])) leftIdx--;
    let rightIdx = idx + 1;
    while (rightIdx < order.length && this.liftedNodeIds.has(order[rightIdx])) rightIdx++;

    if (leftIdx >= 0) {
      const leftId = order[leftIdx];
      const span = this.settledLayout.get(leftId);
      if (span && this.groupLeft <= span.pxStart + span.pxWidth / 2) {
        return { anchorNodeId: leftId, insertBefore: true };
      }
    }
    if (rightIdx < order.length) {
      const rightId = order[rightIdx];
      const span = this.settledLayout.get(rightId);
      if (span && this.groupRight >= span.pxStart + span.pxWidth / 2) {
        return { anchorNodeId: rightId, insertBefore: false };
      }
    }

    return this.pendingAnchor;
  }

  // ─── Visuals ────────────────────────────────────────────────────

  private beginDragVisuals(): void {
    if (!this.draggedBoxEl?.parentNode || !this.draggedNodeId) return;
    const container = this.draggedBoxEl.parentNode as unknown as SVGGElement;
    this.boxesGroupEl = container;
    this.currentDeltaX = 0;
    this.lastClientX = this.startX;
    this.totalPanAppliedPx = 0;

    const svgEl = this.draggedBoxEl.closest('svg');
    if (svgEl) {
      const rect = svgEl.getBoundingClientRect();
      this.viewportLeft = rect.left;
      this.viewportRight = rect.right;
    }

    const boxEls = Array.from(container.querySelectorAll<SVGGElement>(':scope > .timeline-box'));
    const byNode = new Map<string, SVGGElement[]>();
    for (const el of boxEls) {
      const datum = d3.select(el).datum() as BoxDatum | undefined;
      const nodeId = datum?.box.step.node.id;
      if (!nodeId) continue;
      let group = byNode.get(nodeId);
      if (!group) { group = []; byNode.set(nodeId, group); }
      group.push(el);
    }
    this.boxesByNodeId = byNode;
    this.lastOrder = Array.from(byNode.keys());

    // Baseline settledLayout for everyone, straight from their bound
    // data — nothing's moved yet, so this is unconditionally accurate.
    for (const [nodeId, els] of byNode) {
      let minStart = Infinity;
      let maxEnd = -Infinity;
      for (const el of els) {
        const d = d3.select(el).datum() as BoxDatum;
        minStart = Math.min(minStart, d.pxStart);
        maxEnd = Math.max(maxEnd, d.pxStart + d.pxWidth);
      }
      this.settledLayout.set(nodeId, { pxStart: minStart, pxWidth: maxEnd - minStart });
    }

    const placeholder = d3.select(container).insert('rect', ':first-child')
      .attr('class', 'timeline-drag-placeholder')
      .attr('rx', 4)
      .attr('height', PLACEHOLDER_HEIGHT_PX)
      .style('fill', 'var(--accent-alpha-13)')
      .style('stroke', 'var(--accent)')
      .style('stroke-width', 2)
      .style('stroke-dasharray', '4,3')
      .style('pointer-events', 'none');
    this.placeholderEl = placeholder.node();

    this.liftNode(this.draggedNodeId);
    // Initial landing slot: exactly where the dragged block already
    // is — nothing's moved yet. Later ticks reposition this via
    // applyHypotheticalLayout(), not layoutLiftedGroup() (see there).
    // x spans the full slot width from its LEFT edge (no xOffset — a
    // width-spanning rect, unlike a circle's own single-point center;
    // see LiftedBlock.xOffset's own comment), y is the row's own
    // CENTER minus half this rect's height, so it ends up vertically
    // centered within the row exactly like the circle it's standing in
    // for.
    const draggedBlock = this.liftedBlocks.get(this.draggedNodeId);
    if (draggedBlock) {
      placeholder
        .attr('x', this.draggedBlockOriginalLeft)
        .attr('y', draggedBlock.y - PLACEHOLDER_HEIGHT_PX / 2)
        .attr('width', draggedBlock.blockWidth);
    }
    this.layoutLiftedGroup();
  }

  private liftNode(nodeId: string): void {
    if (this.liftedNodeIds.has(nodeId)) return;
    const els = this.boxesByNodeId.get(nodeId);
    if (!els || els.length === 0) return;

    const datums = els.map(el => d3.select(el).datum() as BoxDatum);
    const blockLeft = datums[0].pxStart;
    const last = datums[datums.length - 1];
    const blockWidth = last.pxStart + last.pxWidth - blockLeft;
    const elLeftOffsets = datums.map(d => d.pxStart - blockLeft);
    // Row's own CENTER (not its top) — see LiftedBlock.y's own comment.
    const y = datums[0].box.row * this.rowHeightPx + this.rowHeightPx / 2;
    // Every box here shares the same (uniform) pxWidth — half of it is
    // the left-edge-to-center distance, same correction for all of them.
    const xOffset = datums[0].pxWidth / 2;

    for (const el of els) {
      d3.select(el).raise();
      el.style.filter = 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))';
    }

    this.liftedBlocks.set(nodeId, { els, elLeftOffsets, blockWidth, y, xOffset });
    this.liftedNodeIds.add(nodeId);
    if (nodeId === this.draggedNodeId) this.draggedBlockOriginalLeft = blockLeft;
  }

  /** Returns `nodeId`'s boxes to normal layout — the mirror of liftNode(). Never called for the dragged node itself. */
  private releaseNode(nodeId: string): void {
    const block = this.liftedBlocks.get(nodeId);
    if (!block) return;
    for (const el of block.els) {
      el.style.filter = '';
    }
    this.liftedBlocks.delete(nodeId);
    this.liftedNodeIds.delete(nodeId);
  }

  /**
   * Positions every currently-lifted box: the dragged node's own block
   * tracks the pointer 1:1 (left = its original left + currentDeltaX,
   * no snapping), and every attached cascaded dependency stacks rigidly
   * to its left or right — whichever side it falls on in lastOrder —
   * using each block's own frozen width, so the whole group visibly
   * moves together as one unit. Does NOT touch the placeholder — that
   * marks the hypothetical LANDING slot (wherever the group would end
   * up if dropped now), which is generally a different position from
   * wherever it's currently floating under the cursor; see
   * applyHypotheticalLayout() for where it's actually positioned.
   */
  private layoutLiftedGroup(): void {
    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId) return;
    const draggedBlock = this.liftedBlocks.get(draggedNodeId);
    if (!draggedBlock) return;

    const draggedLeft = this.draggedBlockOriginalLeft + this.currentDeltaX;
    this.setBlockTransform(draggedBlock, draggedLeft);

    const sequence = (this.lastOrder.length > 0 ? this.lastOrder : [draggedNodeId])
      .filter(id => this.liftedNodeIds.has(id));
    const idx = sequence.indexOf(draggedNodeId);
    if (idx === -1) {
      this.groupLeft = draggedLeft;
      this.groupRight = draggedLeft + draggedBlock.blockWidth;
      return;
    }

    let left = draggedLeft;
    this.groupLeft = draggedLeft;
    for (let i = idx - 1; i >= 0; i--) {
      const block = this.liftedBlocks.get(sequence[i]);
      if (!block) continue;
      left -= block.blockWidth;
      this.setBlockTransform(block, left);
      this.groupLeft = left;
    }

    left = draggedLeft + draggedBlock.blockWidth;
    this.groupRight = left;
    for (let i = idx + 1; i < sequence.length; i++) {
      const block = this.liftedBlocks.get(sequence[i]);
      if (!block) continue;
      this.setBlockTransform(block, left);
      left += block.blockWidth;
      this.groupRight = left;
    }
  }

  private setBlockTransform(block: LiftedBlock, blockLeft: number): void {
    block.els.forEach((el, i) => {
      el.setAttribute('transform', `translate(${blockLeft + block.elLeftOffsets[i] + block.xOffset},${block.y})`);
    });
  }

  /**
   * currentDeltaX is driven by two independent sources — real pointer
   * moves (lastClientX) and the auto-scroll RAF loop's cumulative pan
   * compensation (totalPanAppliedPx) — which both keep firing at once
   * during auto-scroll (the pointer doesn't need to move for the RAF
   * loop to run, but it also doesn't STOP moving just because autoscroll
   * is active). Recomputing from scratch in only one of the two call
   * sites would discard whatever the other had accumulated, so both
   * route through this single formula instead.
   */
  private recomputeCurrentDeltaX(): void {
    this.currentDeltaX = (this.lastClientX - this.startX) - this.totalPanAppliedPx;
  }

  /** Pins the dragged block to the pointer, 1:1, every move — see draggedBlockOriginalLeft's doc comment. */
  private updateDraggedPosition(clientX: number): void {
    if (!this.draggedNodeId || !this.liftedBlocks.has(this.draggedNodeId)) return;
    this.lastClientX = clientX;
    this.recomputeCurrentDeltaX();
    this.layoutLiftedGroup();
  }

  /**
   * Auto-pan near either edge of the timeline's own viewport (not the
   * content's start/end — the VISIBLE window), same mechanics as
   * PreparationDragController's updateAutoScroll(): an RAF loop that
   * keeps panning while the cursor stays within AUTOSCROLL_MARGIN of
   * the edge, stopping as soon as it moves back toward the middle (or
   * the drag ends). Panning the WHOLE view shifts the dragged group's
   * screen position too (its on-screen x is content-space pxStart PLUS
   * the pan offset) — callbacks.panBy()'s actually-applied delta is
   * accumulated into totalPanAppliedPx and folded into currentDeltaX via
   * recomputeCurrentDeltaX() each tick to cancel that out, so the group
   * stays pinned under a stationary pointer while the content scrolls
   * underneath it, exactly like the vertical version's position:fixed
   * decoupling achieves for free. Real pointer-move events keep firing
   * during auto-scroll too (the cursor sits still near the edge, but
   * "still" isn't "stopped moving"), so both paths must share the same
   * formula — see recomputeCurrentDeltaX().
   */
  private updateAutoPan(clientX: number): void {
    if (clientX < this.viewportLeft + AUTOSCROLL_MARGIN) {
      this.autoScrollDir = -1;
    } else if (clientX > this.viewportRight - AUTOSCROLL_MARGIN) {
      this.autoScrollDir = 1;
    } else {
      this.autoScrollDir = 0;
    }

    if (this.autoScrollDir !== 0 && this.autoScrollRAF === null) {
      const step = () => {
        if (this.autoScrollDir === 0 || !this.dragging) {
          this.autoScrollRAF = null;
          return;
        }
        // dir -1 (near the left edge) reveals more content to the left
        // — a positive panBy (content shifts right on screen).
        const applied = this.callbacks.panBy(-this.autoScrollDir * AUTOSCROLL_SPEED);
        this.totalPanAppliedPx += applied;
        this.recomputeCurrentDeltaX();
        this.layoutLiftedGroup();
        this.autoScrollRAF = requestAnimationFrame(step);
      };
      this.autoScrollRAF = requestAnimationFrame(step);
    } else if (this.autoScrollDir === 0 && this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
  }

  /**
   * Reorders the still-in-place boxes to match `nodeOrder` by animating
   * each one's x position to its new computed pxStart (via the view-
   * provided layoutForOrder() callback) — a plain D3 transition on the
   * `transform` attribute, no manual FLIP trick needed: unlike the
   * Elaboració panel's DOM-flow rows, a box's screen position here is
   * ALREADY just whatever we set its transform to, so there's no
   * separate "let the browser lay it out, then animate from there" step.
   */
  private applyHypotheticalLayout(nodeOrder: string[]): void {
    if (!this.boxesGroupEl) return;

    const segmentLayout = this.callbacks.layoutForOrder(nodeOrder);
    const nodeSpans = new Map<string, { pxStart: number; pxWidth: number }>();

    for (const [nodeId, els] of this.boxesByNodeId) {
      if (this.liftedNodeIds.has(nodeId)) continue;
      let minStart = Infinity;
      let maxEnd = -Infinity;
      for (const el of els) {
        const d = d3.select(el).datum() as BoxDatum;
        const pos = segmentLayout.get(d.box.step.stepNumber);
        if (!pos) continue;
        minStart = Math.min(minStart, pos.pxStart);
        maxEnd = Math.max(maxEnd, pos.pxStart + pos.pxWidth);
        // Circle CENTER, not the slot's own left edge/top — same
        // correction as setBlockTransform()/liftNode(), see
        // LiftedBlock.y/.xOffset's own comments for why it's needed.
        const x = pos.pxStart + pos.pxWidth / 2;
        const y = d.box.row * this.rowHeightPx + this.rowHeightPx / 2;
        d3.select(el).transition().duration(MOVE_DURATION_MS)
          .attr('transform', `translate(${x},${y})`);
      }
      if (minStart !== Infinity) nodeSpans.set(nodeId, { pxStart: minStart, pxWidth: maxEnd - minStart });
    }
    for (const [nodeId, span] of nodeSpans) this.settledLayout.set(nodeId, span);

    // The placeholder's landing slot: the combined span of every
    // CURRENTLY lifted node's hypothetical position in this SAME order
    // (always contiguous, per stepOrder.ts's own cascade behaviour) —
    // deliberately not the floating group's own cursor-tracking
    // position (see layoutLiftedGroup()'s doc comment for why those
    // two are different things).
    if (this.placeholderEl) {
      let phStart = Infinity;
      let phEnd = -Infinity;
      for (const [nodeId, els] of this.boxesByNodeId) {
        if (!this.liftedNodeIds.has(nodeId)) continue;
        for (const el of els) {
          const d = d3.select(el).datum() as BoxDatum;
          const pos = segmentLayout.get(d.box.step.stepNumber);
          if (!pos) continue;
          phStart = Math.min(phStart, pos.pxStart);
          phEnd = Math.max(phEnd, pos.pxStart + pos.pxWidth);
        }
      }
      if (phStart !== Infinity) {
        const draggedBlock = this.draggedNodeId ? this.liftedBlocks.get(this.draggedNodeId) : undefined;
        // Same row-center-minus-half-height placement as the initial
        // placeholder setup in beginDragVisuals() — see its own comment.
        const y = draggedBlock ? draggedBlock.y - PLACEHOLDER_HEIGHT_PX / 2 : 0;
        d3.select(this.placeholderEl)
          .attr('x', phStart)
          .attr('y', y)
          .attr('width', Math.max(phEnd - phStart, 1));
      }
    }
  }

  // ─── Cleanup ────────────────────────────────────────────────────

  private cleanup(): void {
    const wasDragging = this.dragging;

    this.placeholderEl?.remove();
    this.placeholderEl = null;
    for (const block of this.liftedBlocks.values()) {
      for (const el of block.els) el.style.filter = '';
    }

    if (this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
    this.autoScrollDir = 0;

    this.dragging = false;
    this.draggedNodeId = null;
    this.draggedBoxEl = null;
    this.boxesGroupEl = null;
    this.boxesByNodeId = new Map();
    this.liftedBlocks.clear();
    this.liftedNodeIds.clear();
    this.settledLayout.clear();
    this.draggedBlockOriginalLeft = 0;
    this.groupLeft = 0;
    this.groupRight = 0;
    this.currentDeltaX = 0;
    this.lastClientX = 0;
    this.totalPanAppliedPx = 0;
    this.viewportLeft = 0;
    this.viewportRight = 0;
    this.lastOrder = [];
    this.pendingAnchor = null;
    this.lastResolvedKey = null;

    if (wasDragging) this.callbacks.onDragStateChange(false);
  }
}

